import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, Radar, Eye, Satellite, Map, Bot, ShieldCheck, Wrench, Package, BookOpen, LifeBuoy, PhoneCall } from 'lucide-react';
import { Reveal } from './shared';
import { CATEGORIES } from '../../data/categories';
import { trackEvent } from '../../lib/analytics';

const WA = 'https://wa.me/917011370322';

export const FAMILIES = [
  {
    key: 'smartbot',
    name: 'SMARTBOT',
    title: 'Intelligent Floor Cleaning Robots',
    desc: 'Robot vacuums, vacuum + mop combos and autonomous floor robots for homes and commercial spaces.',
    caps: 'Vacuum • Mop • AI Navigation • Smart Mapping',
    href: '/floor-cleaning-robots',
    image: CATEGORIES[0].image,
    accent: 'text-emerald-400',
    bar: 'bg-emerald-400',
    border: 'hover:border-emerald-500/40',
  },
  {
    key: 'shinebot',
    name: 'SHINEBOT',
    title: 'Intelligent Window Cleaning Robots',
    desc: 'Automatic glass cleaning for framed and frameless windows — from apartments to high-rise commercial glass.',
    caps: 'Glass Cleaning • Smart Navigation • Automated Cleaning',
    href: '/window-cleaning-robots',
    image: CATEGORIES[2].image,
    accent: 'text-cyan-400',
    bar: 'bg-cyan-400',
    border: 'hover:border-cyan-500/40',
  },
  {
    key: 'poolbot',
    name: 'POOLBOT',
    title: 'Intelligent Pool Cleaning Robots',
    desc: 'Wall-climbing, deep-filtration robots for residential pools, hotels and resorts. Home of Robo Titan & Robo Dressel.',
    caps: 'Smart Navigation • Wall Climbing • Deep Filtration',
    href: '/pool-cleaning-robots',
    image: CATEGORIES[1].image,
    accent: 'text-blue-400',
    bar: 'bg-blue-400',
    border: 'hover:border-blue-500/40',
  },
  {
    key: 'lawnbot',
    name: 'LAWNBOT',
    title: 'Intelligent Robotic Lawn Mowers',
    desc: 'Autonomous grass cutting with RTK precision and AI Vision for residential and commercial lawns. Home of Greenova.',
    caps: 'RTK • AI Vision • Precision Cutting',
    href: '/robotic-lawn-mowers',
    image: CATEGORIES[3].image,
    accent: 'text-green-400',
    bar: 'bg-green-400',
    border: 'hover:border-green-500/40',
  },
];

export const BrandHero = () => (
  <section className="relative bg-[#0a0a0a] text-white overflow-hidden" data-testid="hv2-brand-hero">
    <div className="absolute inset-0 hv2-noise" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-16 md:pb-20 text-center">
      <Reveal>
        <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.35em] font-bold text-white/50 mb-6">Harpalyk Robotics</p>
      </Reveal>
      <Reveal delay={100}>
        <h1 className="font-outfit text-5xl md:text-7xl lg:text-8xl font-medium leading-none tracking-tighter">
          Intelligent Robotics.
          <br />
          <span className="text-white/40">Made for India.</span>
        </h1>
      </Reveal>
      <Reveal delay={200}>
        <p className="font-manrope text-base md:text-lg text-white/50 mt-7 max-w-2xl mx-auto leading-relaxed">
          Smart autonomous robots for floors, windows, pools and lawns — designed to make everyday cleaning effortless.
        </p>
      </Reveal>
      <Reveal delay={300}>
        <div className="flex flex-wrap items-center justify-center gap-4 mt-9">
          <a
            href="#robot-families"
            onClick={() => trackEvent('brand_hero_explore')}
            data-testid="brand-hero-explore-btn"
            className="font-manrope bg-[#0057B8] hover:bg-[#004494] text-white px-8 py-4 rounded-full font-semibold transition-colors"
          >
            Explore Our Robots
          </a>
          <a
            href="#contact"
            onClick={() => trackEvent('brand_hero_demo')}
            data-testid="brand-hero-demo-btn"
            className="font-manrope border border-white/25 hover:bg-white hover:text-black text-white px-8 py-4 rounded-full font-semibold transition-colors"
          >
            Book a Demo
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export const FamilyCards = () => (
  <section id="robot-families" className="bg-[#0a0a0a] text-white pb-24 md:pb-36 pt-4" data-testid="hv2-family-cards">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <Reveal>
        <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.35em] font-bold text-white/40">Meet Harpalyk Robotics</p>
      </Reveal>
      <Reveal delay={100}>
        <h2 className="font-outfit text-4xl md:text-6xl font-medium tracking-tighter mt-4 mb-14">
          Four Robot Families.
          <br />
          <span className="text-white/40">One Intelligent Ecosystem.</span>
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {FAMILIES.map((f, i) => (
          <Reveal key={f.key} delay={i * 100}>
            <Link
              to={f.href}
              onClick={() => trackEvent(`family_card_${f.key}`)}
              data-testid={`family-card-${f.key}`}
              className={`group flex flex-col h-full bg-[#0a0a0a] p-7 md:p-8 border border-transparent transition-colors duration-300 ${f.border}`}
            >
              <div className="h-44 mb-7 overflow-hidden bg-[#141414] flex items-center justify-center">
                <img
                  src={f.image}
                  alt={`Harpalyk ${f.name} — ${f.title}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <span className={`block w-8 h-[3px] ${f.bar} mb-4`} aria-hidden="true" />
              <h3 className={`font-outfit text-2xl font-semibold tracking-tight ${f.accent}`}>{f.name}</h3>
              <p className="font-outfit text-base text-white mt-1">{f.title}</p>
              <p className="font-manrope text-sm text-white/45 mt-3 leading-relaxed flex-1">{f.desc}</p>
              <p className="font-manrope text-[11px] uppercase tracking-[0.15em] text-white/35 mt-5">{f.caps}</p>
              <span className="inline-flex items-center gap-1.5 font-manrope text-sm font-semibold text-white mt-5">
                Explore {f.name}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const TECH = [
  { icon: Cpu, title: 'AI Navigation', desc: 'Intelligent movement, obstacle handling and route optimization in real time.' },
  { icon: Radar, title: 'LiDAR Mapping', desc: 'Precise laser mapping for efficient, complete room-by-room navigation.' },
  { icon: Eye, title: 'AI Vision', desc: 'Visual recognition that identifies obstacles, edges and terrain intelligently.' },
  { icon: Satellite, title: 'RTK Precision', desc: 'Centimeter-level outdoor positioning for boundary-wire-free lawn mowing.' },
  { icon: Map, title: 'Smart Mapping', desc: 'Efficient route planning across homes, pools, glass and large facilities.' },
  { icon: Bot, title: 'Autonomous Cleaning', desc: 'Scheduled, self-charging, self-resuming cleaning with minimal manual effort.' },
];

export const Innovation = () => (
  <section id="innovation" className="bg-[#0a0a0a] text-white py-28 md:py-40 px-6 md:px-12" data-testid="hv2-innovation">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.35em] font-bold text-white/40">Technology</p>
      </Reveal>
      <Reveal delay={100}>
        <h2 className="font-outfit text-4xl md:text-6xl font-medium tracking-tighter mt-4 mb-14">INNOVATION THAT WORKS.</h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {TECH.map((t, i) => (
          <Reveal key={t.title} delay={i * 80}>
            <div className="bg-[#0a0a0a] p-8 h-full" data-testid={`innovation-${t.title.toLowerCase().replace(/\s/g, '-')}`}>
              <t.icon className="w-7 h-7 text-[#00AEEF]" aria-hidden="true" />
              <h3 className="font-outfit text-xl font-semibold tracking-tight mt-5">{t.title}</h3>
              <p className="font-manrope text-sm text-white/45 mt-2 leading-relaxed">{t.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CARE = [
  { icon: ShieldCheck, title: 'Warranty', desc: '1 or 2-year warranty on robots', href: '/warranty-policy' },
  { icon: Wrench, title: 'Installation & Service', desc: 'Free installation, pan-India service', href: '/shipping-policy' },
  { icon: Package, title: 'Spare Parts', desc: 'Genuine consumables & accessories', href: `${WA}?text=${encodeURIComponent('Hello Harpalyk, I need genuine spare parts for my robot.')}` },
  { icon: BookOpen, title: 'Manuals', desc: 'Request product manuals & guides', href: `${WA}?text=${encodeURIComponent('Hello Harpalyk, please share the manual for my robot.')}` },
  { icon: LifeBuoy, title: 'Troubleshooting', desc: 'Maintenance tips & how-to guides', href: '/blog/robot-vacuum-maintenance-tips-long-life' },
  { icon: PhoneCall, title: 'Contact Support', desc: 'Talk to our support team', href: '/support' },
];

export const HarpalykCare = () => (
  <section className="bg-white text-gray-900 py-28 md:py-36 px-6 md:px-12" data-testid="hv2-care">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.35em] font-bold text-[#0057B8]">Harpalyk Care</p>
      </Reveal>
      <Reveal delay={100}>
        <h2 className="font-outfit text-4xl md:text-6xl font-medium tracking-tighter mt-4 mb-4">OWNERSHIP, CARED FOR.</h2>
      </Reveal>
      <Reveal delay={150}>
        <p className="font-manrope text-lg text-gray-500 max-w-2xl mb-14">
          Every Harpalyk robot is backed by a complete support ecosystem — from installation to genuine spare parts.
        </p>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/5 border border-black/5">
        {CARE.map((c, i) => (
          <Reveal key={c.title} delay={i * 60}>
            <a
              href={c.href}
              target={c.href.startsWith('https://wa.me') ? '_blank' : undefined}
              rel={c.href.startsWith('https://wa.me') ? 'noopener noreferrer' : undefined}
              data-testid={`care-${c.title.toLowerCase().replace(/[\s&]+/g, '-')}`}
              className="group flex items-start gap-4 bg-white p-7 h-full hover:bg-[#f5f5f7] transition-colors duration-300"
            >
              <c.icon className="w-6 h-6 text-[#0057B8] mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span>
                <span className="font-outfit text-lg font-semibold tracking-tight block">{c.title}</span>
                <span className="font-manrope text-sm text-gray-500 mt-1 block">{c.desc}</span>
              </span>
              <ArrowRight size={16} className="ml-auto mt-1 text-gray-300 group-hover:text-gray-900 group-hover:translate-x-1 transition-all duration-300" />
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
